<template>
	<div>
		<Restore @success="close('Restore')" v-slot="{ restore }">
			<Page animate>
				<template #content>
					<safe-area-top class="absolute" @close="onClose" closePosition="left" :theme="closeTheme"> </safe-area-top>
					<top-video :with-video="withVideo"></top-video>

					<div class="container">
						<div class="title">This is your final chance</div>

						<div class="advantages">
							<div class="advantage"><i>⏰</i> <span>Offer only available today</span></div>
							<div class="advantage"><i>🔥</i> <span>One year of Unlimited Access for 40% off</span></div>
						</div>

						<Products :products="products" :active="activeProduct" @select="handleSelect" />

						<PaywallLinksBlock @restore="restore" />
					</div>

					<Prompt
						:isOpen="showPaywallCancelPrompt"
						title="Purchase Failed"
						text="Cannot connect to the iTunes Store. Please check your internet connection and try again"
						action="Retry"
						@onClose="onClosePaywallCancelPrompt"
						@onAction="onActionPaywallCancelPrompt"
					/>

					<component
						:is="upgradeModalComponent"
						:isOpen="showUpgradeModal"
						@upgrade="onUpgradeToBottomOffer"
						@skip="onSkipToBottomOffer"
					></component>
				</template>

				<template #footer>
					<div class="container">
						<p-button @click.prevent="handleSubmit" label="" :loading="isLoading" cls="btn-primary btn-block">
							CONTINUE
						</p-button>
					</div>
				</template>
			</Page>
		</Restore>
	</div>
</template>

<script>
import Page from '@/components/Page'
import Restore from '@/components/Restore'
import PaywallLinksBlock from '@/components/PaywallLinksBlock'
import TopVideo from '@/components/Video'
import CommentsSlider from '@/components/CommentsSlider'
import Alert from '@/components/Alert'
import Prompt from '@/components/Prompt'
import PButton from '@/components/Button'
import Products from '@/components/products/ios/Products'
import { computed, ref } from '@vue/reactivity'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import useApi from '@/composition/api.js'
import useConfig from '@/composition/config.js'
import SafeAreaTop from '../../components/SafeAreaTop.vue'
import UpgradeToYearlyModal from '@/components/UpgradeToYearlyModal.vue'
import UpgradeToQuarterlyModal from '@/components/UpgradeToQuarterlyModal.vue'
import { inject, onMounted, watch } from '@vue/runtime-core'

export default {
	components: {
		Page,
		TopVideo,
		CommentsSlider,
		Products,
		SafeAreaTop,
		PButton,
		Alert,
		Prompt,
		UpgradeToYearlyModal,
		UpgradeToQuarterlyModal,
		Restore,
		PaywallLinksBlock
	},

	setup() {
		const store = useStore()
		const api = useApi()
		const config = useConfig()
		const router = useRouter()
		const amplitude = inject('amplitude')
		const { terms, privacy } = api

		const upgradeModalComponent = ref(null)
		const showUpgradeModal = ref(false)

		const products = computed(() =>
			store.getters['products/getData']([
				Object.assign(
					{
						id: 'simple_original_subscription_3_months',
						text: '12 Weeks - [price]/12-week',
						subtitle: '[weekly_price]/week',
						badge: 'SAVE 40%',
						terms: 'Subscription terms: a 12-week subscription fee is [price] and auto-renews each 12 weeks'
					},
					config.get({ key: 'paywall.top_product', default_value: {} })
				),
				Object.assign(
					{
						id: 'simple_original_subscription_12_months',
						text: 'Annually - [price]/year',
						subtitle: '[weekly_price]/week',
						terms: 'Subscription terms: an annual subscription fee is [price] and auto-renews yearly.'
					},
					config.get({ key: 'paywall.bottom_product', default_value: {} })
				)
			])
		)

		let activeProduct = ref(products.value[0])
		watch(products, () => {
			activeProduct.value = products.value[0]
		})
		let isLoading = computed(() => store.state.isLoading)

		let showPaywallCancelPrompt = ref(false)
		const next = computed(() => config.get({ key: 'postPaid.type', default_value: 'offer-92off-workbooks-bundle' }))
		const closeTheme = computed(() => config.get({ key: 'paywall.close_button_theme', default_value: 'dark' }))
		const withVideo = computed(() => config.get({ key: 'paywall.with_video', default_value: true }))

		const handleSelect = (product) => {
			activeProduct.value = product
		}

		const close = (Status = 'Close', product = {}) => {
			amplitude.logEvent('Paywall - Close', {
				Layout: 'Offer 92% off',
				From: store.getters['getSource'],
				Step: 'Paywall',
				Status
			})
			amplitude.logEvent(store.getters['getAmplitudeEventName']('PaywallDone'), {
				action: Status,
				...product
			})
			router.push({
				name: next.value
			})
		}

		const onClose = () => close()

		const purchase = (product) => {
			api.purchase({
				productId: product.id,
				onFinish: () => {
					amplitude.logEvent('Paywall - Done', {
						Layout: 'Offer 92% off',
						From: store.getters['getSource'],
						Step: 'Paywall',
						ProductId: product.id,
						Status: 'Subscribe'
					})
					close('Subscribe', {
						product_id: product.id,
						duration_month: product.duration_months,
						price: Math.abs(product.product_price),
						currency: product.currency
					})
				},
				onError: () => {
					console.log('error')
				},
				onCancel: () => {
					showPaywallCancelPrompt.value = true
				},
				closeOnSuccess: false
			})
		}

		const onClosePaywallCancelPrompt = () => {
			showPaywallCancelPrompt.value = false
		}

		const onActionPaywallCancelPrompt = () => {
			showPaywallCancelPrompt.value = false
			purchase(activeProduct.value)
		}

		const handleSubmit = () => {
			if (activeProduct.value.upgrade_to_bottom_modal) {
				upgradeModalComponent.value = activeProduct.value.upgrade_to_bottom_modal
				showUpgradeModal.value = true
				return false
			}
			purchase(activeProduct.value)
		}

		const onUpgradeToBottomOffer = () => {
			activeProduct.value = products.value[1]
			showUpgradeModal.value = false
			purchase(activeProduct.value)
		}

		const onSkipToBottomOffer = () => {
			showUpgradeModal.value = false
			purchase(activeProduct.value)
		}

		onMounted(() => {
			amplitude.logEvent(store.getters['getAmplitudeEventName']('PaywallShow'), {
				product_ids: products.value.map((product) => product.id),
				prices: products.value.map((p) => p?.product_price),
				currency: activeProduct.value?.currency?.toLowerCase(),
				data_source: 'client'
			})
		})

		return {
			products,
			activeProduct,
			isLoading,
			withVideo,
			handleSelect,
			handleSubmit,
			terms,
			privacy,
			showPaywallCancelPrompt,
			onClosePaywallCancelPrompt,
			onActionPaywallCancelPrompt,
			onClose,
			closeTheme,
			upgradeModalComponent,
			showUpgradeModal,
			onUpgradeToBottomOffer,
			onSkipToBottomOffer,
			close
		}
	}
}
</script>

<style scoped lang="scss">
.title {
	font-weight: bold;
	font-size: 2rem;
	text-align: center;
	margin-top: 1.3rem;
}

.absolute {
	position: absolute;
	top: 0;
	left: 0;
	z-index: 2;
	width: 100%;
}

.advantages {
	margin-top: 2rem;

	@media (max-width: 400px) and (max-height: 700px) {
		margin-top: 1.5rem;
	}
}

.advantage {
	font-size: 1.2rem;
	color: #3b3c57;
	display: flex;
	margin-bottom: 0.8rem;

	@media (max-width: 400px) {
		font-size: 1rem;
	}

	i {
		display: inline-block;
		text-align: center;
		width: 2.5rem;
		font-size: 1.3em;
		margin-right: 0.5rem;
		font-style: normal;
		position: relative;
		bottom: 3px;
	}

	span {
		width: calc(100% - 2.5rem);
	}
}
</style>
